import api from './api';

/**
 * Centralized logout handler for all user types
 * Can be used anywhere in the application
 */
class LogoutManager {
  constructor() {
    this.isLoggingOut = false;
    this.logoutCallbacks = [];
    
    // Comprehensive list of all keys that need to be cleared
    this.storageKeys = {
      auth: [
        'token',
        'accessToken', 
        'refreshToken',
        'sessionId',
        'user',
        'userId',
        'userRole',
        'tenantId',
        'isSuperAdmin',
        'permissions',
        'authUser'
      ],
      tenant: [
        'tenantData',
        'hoi-tenant-data',
        'tenant',
        'tenantInfo',
        'institutionId',
        'institutionName',
        'organizationId',
        'organizationName'
      ],
      subscription: [
        'subscription',
        'subscriptionData',
        'planName',
        'planId',
        'subscriptionStatus'
      ],
      payment: [
        'rzp_checkout_anon_id',
        'rzp_device_id', 
        'rzp_stored_checkout_id',
        'razorpay_payment_id',
        'razorpay_order_id'
      ],
      session: [
        'sessionId',
        'lastLogin',
        'loginTime',
        'activeSessions'
      ],
      application: [
        'pendingApplication',
        'applicationStatus',
        'applicationId'
      ],
      userPreferences: [
        'theme',
        'language',
        'preferences',
        'rememberedEmail',
        'pendingUser'
      ],
      all: [] // Will be populated dynamically
    };
    
    // Build the complete list
    this.buildCompleteKeyList();
  }

  /**
   * Build complete list of all keys to clear
   */
  buildCompleteKeyList() {
    const allKeys = [];
    Object.values(this.storageKeys).forEach(keys => {
      if (Array.isArray(keys)) {
        allKeys.push(...keys);
      }
    });
    // Remove duplicates
    this.storageKeys.all = [...new Set(allKeys)];
  }

  /**
   * Register a callback to be called before logout
   */
  onBeforeLogout(callback) {
    if (typeof callback === 'function') {
      this.logoutCallbacks.push(callback);
    }
    return this;
  }

  /**
   * Clear all registered callbacks
   */
  clearCallbacks() {
    this.logoutCallbacks = [];
    return this;
  }

  /**
   * Execute all before logout callbacks
   */
  async executeCallbacks() {
    for (const callback of this.logoutCallbacks) {
      try {
        await callback();
      } catch (error) {
        console.warn('Logout callback error:', error);
        // Continue with logout even if callback fails
      }
    }
  }

  /**
   * Get current user info from localStorage
   */
  getUserInfo() {
    try {
      // Try multiple sources for user info
      let user = null;
      let userStr = localStorage.getItem('user');
      
      if (userStr) {
        try {
          user = JSON.parse(userStr);
        } catch (e) {
          // Not JSON, use as string
          user = { email: userStr };
        }
      }
      
      // Try tenant data
      let tenantData = null;
      const tenantStr = localStorage.getItem('tenantData') || localStorage.getItem('hoi-tenant-data');
      if (tenantStr) {
        try {
          tenantData = JSON.parse(tenantStr);
        } catch (e) {
          // Not JSON, ignore
        }
      }
      
      // Try subscription data
      let subscription = null;
      const subStr = localStorage.getItem('subscription');
      if (subStr) {
        try {
          subscription = JSON.parse(subStr);
        } catch (e) {
          // Not JSON, ignore
        }
      }
      
      return {
        id: user?.id || localStorage.getItem('userId'),
        email: user?.email || localStorage.getItem('userEmail'),
        role: localStorage.getItem('userRole') || user?.role,
        tenantId: localStorage.getItem('tenantId') || user?.tenantId || tenantData?.institutionId,
        isSuperAdmin: user?.isSuperAdmin || false,
        user: user,
        tenantData: tenantData,
        subscription: subscription,
        sessionId: localStorage.getItem('sessionId'),
        token: localStorage.getItem('token') || localStorage.getItem('accessToken')
      };
    } catch (error) {
      console.error('Error getting user info:', error);
      return {
        id: null,
        email: null,
        role: null,
        tenantId: null,
        isSuperAdmin: false,
        user: null,
        tenantData: null,
        subscription: null,
        sessionId: null,
        token: null
      };
    }
  }

  /**
   * Clear all localStorage items
   */
  clearLocalStorage() {
    try {
      // Get all keys before clearing (for logging)
      const allKeys = Object.keys(localStorage);
      console.log(`📊 Found ${allKeys.length} items in localStorage`);
      
      // Log what we're clearing
      const keysToClear = this.storageKeys.all;
      console.log('🗑️ Clearing keys:', keysToClear);
      
      // Remove specific keys
      let clearedCount = 0;
      keysToClear.forEach(key => {
        try {
          if (localStorage.getItem(key) !== null) {
            localStorage.removeItem(key);
            clearedCount++;
          }
        } catch (e) {
          console.warn(`Failed to remove ${key}:`, e);
        }
      });
      
      // Also clear any keys that start with specific prefixes
      const prefixes = ['rzp_', 'tenant_', 'subscription_', 'session_'];
      prefixes.forEach(prefix => {
        allKeys.forEach(key => {
          if (key.startsWith(prefix)) {
            try {
              localStorage.removeItem(key);
              clearedCount++;
            } catch (e) {
              // Ignore
            }
          }
        });
      });
      
      console.log(`✅ Cleared ${clearedCount} items from localStorage`);
      
    } catch (e) {
      console.warn('Failed to clear localStorage:', e);
    }
  }

  /**
   * Clear sessionStorage
   */
  clearSessionStorage() {
    try {
      const allKeys = Object.keys(sessionStorage);
      console.log(`📊 Found ${allKeys.length} items in sessionStorage`);
      
      // Clear specific keys
      this.storageKeys.all.forEach(key => {
        try {
          sessionStorage.removeItem(key);
        } catch (e) {
          // Ignore
        }
      });
      
      // Clear all sessionStorage as fallback
      sessionStorage.clear();
      console.log('✅ sessionStorage cleared');
      
    } catch (e) {
      console.warn('Failed to clear sessionStorage:', e);
      try {
        sessionStorage.clear();
      } catch (err) {
        // Ignore
      }
    }
  }

  /**
   * Clear cookies with specific names
   */
  clearCookies() {
    try {
      const cookies = document.cookie.split(';');
      let clearedCount = 0;
      
      // Specific cookies to clear
      const cookiesToClear = [
        'token',
        'sessionId',
        'refreshToken',
        'subscription',
        'tenantData',
        'hoi-tenant-data',
        'rzp_checkout_anon_id',
        'rzp_device_id',
        'rzp_stored_checkout_id'
      ];
      
      cookies.forEach(cookie => {
        const trimmed = cookie.trim();
        const eqPos = trimmed.indexOf('=');
        const name = eqPos > -1 ? trimmed.substr(0, eqPos).trim() : trimmed.trim();
        
        // Clear if matches or if it's a session cookie
        if (cookiesToClear.includes(name) || name.startsWith('session_') || name.startsWith('rzp_')) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
          clearedCount++;
        }
      });
      
      console.log(`✅ Cleared ${clearedCount} cookies`);
      
    } catch (e) {
      console.warn('Failed to clear cookies:', e);
    }
  }

  /**
   * Clear IndexedDB (if used)
   */
  async clearIndexedDB() {
    try {
      const databases = await indexedDB.databases();
      for (const db of databases) {
        if (db.name) {
          await indexedDB.deleteDatabase(db.name);
        }
      }
      console.log('✅ IndexedDB cleared');
    } catch (e) {
      console.warn('Failed to clear IndexedDB:', e);
    }
  }

  /**
   * Clear all persisted data
   */
  async clearAllData() {
    console.log('🗑️ [LogoutManager] Clearing all persisted data...');
    
    // Clear localStorage
    this.clearLocalStorage();
    
    // Clear sessionStorage
    this.clearSessionStorage();
    
    // Clear cookies
    this.clearCookies();
    
    // Clear IndexedDB (non-blocking)
    try {
      await this.clearIndexedDB();
    } catch (e) {
      console.warn('IndexedDB clear failed:', e);
    }
    
    console.log('✅ All persisted data cleared');
  }

  /**
   * Call logout API
   * Returns { success, data, error }
   */
  async callLogoutAPI(token, sessionId) {
    console.log('📡 [LogoutManager] Calling logout API...');
    
    try {
      // Prepare headers with token if available
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Prepare request body
      const body = {};
      if (sessionId) {
        body.sessionId = sessionId;
      }
      
      // Make the API call
      const response = await api.post('/api/new-auth/logout', body, { headers });
      
      console.log('✅ [LogoutManager] Logout API response:', response.data);
      
      return {
        success: response.data?.success === true,
        data: response.data,
        error: null
      };
      
    } catch (error) {
      console.error('❌ [LogoutManager] Logout API error:', error);
      
      // Try alternative logout endpoint if main fails
      try {
        console.log('📡 [LogoutManager] Trying alternative logout endpoint...');
        const response = await api.post('/api/auth/logout', {}, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        return {
          success: response.data?.success === true,
          data: response.data,
          error: null
        };
      } catch (altError) {
        console.error('❌ [LogoutManager] Alternative logout also failed:', altError);
        return {
          success: false,
          data: null,
          error: error.message || 'Failed to call logout API'
        };
      }
    }
  }

  /**
   * Simple synchronous logout for components
   * This is the version used by LibraryHeader and Dashboard
   */
  simpleLogout(options = {}) {
    const { redirectTo = '/signin' } = options;
    
    console.log('🚪 [LogoutManager] Simple logout initiated');
    
    try {
      // Clear localStorage
      localStorage.clear();
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      // Clear cookies
      document.cookie.split(';').forEach((c) => {
        const name = c.split('=')[0].trim();
        if (name) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
        }
      });
      
      console.log('✅ [LogoutManager] Simple logout completed');
    } catch (error) {
      console.error('❌ [LogoutManager] Simple logout error:', error);
    }
    
    // Redirect
    console.log('🚪 [LogoutManager] Redirecting to:', redirectTo);
    window.location.href = redirectTo;
  }

  /**
   * Perform logout with API call (async version)
   */
  async logout(options = {}) {
    const {
      redirectTo = '/signin',
      showToast = false,
      callbacks = true,
      force = false,
      clearAllData = true,
      skipApiCall = false,
      onSuccess = null,
      onError = null
    } = options;

    // Prevent multiple simultaneous logout attempts
    if (this.isLoggingOut && !force) {
      console.log('⏳ Logout already in progress...');
      return { success: false, message: 'Logout already in progress' };
    }

    this.isLoggingOut = true;

    // Get user info before logout (for logging)
    const userInfo = this.getUserInfo();
    
    console.log('\n🚪 ===========================================');
    console.log('🚪 [LogoutManager] Initiating logout');
    console.log('🚪 ===========================================');
    console.log('📊 User info:', {
      id: userInfo.id,
      email: userInfo.email,
      role: userInfo.role,
      tenantId: userInfo.tenantId,
      isSuperAdmin: userInfo.isSuperAdmin,
      hasTenantData: !!userInfo.tenantData,
      hasSubscription: !!userInfo.subscription,
      sessionId: userInfo.sessionId,
      hasToken: !!userInfo.token
    });

    try {
      // Execute pre-logout callbacks if enabled
      if (callbacks) {
        await this.executeCallbacks();
      }

      // ============================================
      // STEP 1: Call Logout API
      // ============================================
      let apiSuccess = false;
      let apiData = null;
      
      if (!skipApiCall) {
        const apiResult = await this.callLogoutAPI(userInfo.token, userInfo.sessionId);
        apiSuccess = apiResult.success;
        apiData = apiResult.data;
        
        if (apiSuccess) {
          console.log('✅ Logout API call successful');
        } else {
          console.warn('⚠️ Logout API call failed:', apiResult.error);
          // Continue with local logout even if API fails
        }
      } else {
        console.log('⏭️ Skipping logout API call (skipApiCall=true)');
      }

      // ============================================
      // STEP 2: Clear all persisted data
      // ============================================
      if (clearAllData) {
        await this.clearAllData();
      } else {
        // Clear only auth-related data
        this.clearLocalStorage();
        this.clearSessionStorage();
        this.clearCookies();
      }

      // ============================================
      // STEP 3: Clear React state (if using React)
      // ============================================
      if (typeof window.clearAppState === 'function') {
        try {
          window.clearAppState();
        } catch (e) {
          console.warn('Failed to clear app state:', e);
        }
      }

      // ============================================
      // STEP 4: Dispatch logout event
      // ============================================
      const logoutEvent = new CustomEvent('user-logout', { 
        detail: { 
          userInfo, 
          apiSuccess, 
          apiData,
          timestamp: Date.now(),
          method: skipApiCall ? 'local' : 'api'
        }
      });
      document.dispatchEvent(logoutEvent);

      // ============================================
      // STEP 5: Success handling
      // ============================================
      const successMessage = apiSuccess 
        ? 'Logout successful' 
        : skipApiCall 
          ? 'Logged out locally' 
          : 'Logged out locally (server session may still be active)';

      if (showToast && typeof window.showToast === 'function') {
        window.showToast(successMessage, apiSuccess ? 'success' : 'warning');
      }

      console.log('✅ [LogoutManager] Logout complete');
      console.log('📊 Final state:', {
        localStorage: Object.keys(localStorage).length,
        sessionStorage: Object.keys(sessionStorage).length,
        cookies: document.cookie.split(';').length
      });

      // Execute success callback if provided
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess({ 
          apiSuccess, 
          userInfo,
          apiData,
          cleared: true,
          skipApiCall
        });
      }

      // ============================================
      // STEP 6: Redirect
      // ============================================
      setTimeout(() => {
        window.location.href = redirectTo;
      }, 100);

      return { 
        success: true, 
        message: successMessage,
        apiSuccess,
        apiData,
        userInfo,
        redirectTo: redirectTo,
        skipApiCall
      };

    } catch (error) {
      console.error('❌ [LogoutManager] Logout error:', error);

      // ============================================
      // ERROR: Still try to clear data
      // ============================================
      try {
        await this.clearAllData();
      } catch (clearError) {
        console.error('Failed to clear data:', clearError);
      }

      // Execute error callback if provided
      if (onError && typeof onError === 'function') {
        onError(error);
      }

      // Show error toast if enabled
      if (showToast && typeof window.showToast === 'function') {
        window.showToast('Logout completed with some issues', 'warning');
      }

      // Still redirect
      setTimeout(() => {
        window.location.href = redirectTo;
      }, 100);

      return { 
        success: false, 
        error: error.message,
        message: 'Logout completed with issues',
        redirectTo: redirectTo
      };

    } finally {
      this.isLoggingOut = false;
      console.log('🚪 ===========================================\n');
    }
  }

  /**
   * Force logout without API call (for emergency use)
   */
  async forceLogout(redirectTo = '/signin') {
    console.log('🚨 [LogoutManager] Force logout initiated');
    try {
      await this.clearAllData();
    } catch (e) {
      console.error('Force logout clear error:', e);
    }
    window.location.href = redirectTo;
    return { success: true, message: 'Force logout completed' };
  }

  /**
   * Emergency cleanup - clears everything immediately
   */
  emergencyCleanup() {
    try {
      localStorage.clear();
      sessionStorage.clear();
      
      document.cookie.split(';').forEach(cookie => {
        const trimmed = cookie.trim();
        const eqPos = trimmed.indexOf('=');
        const name = eqPos > -1 ? trimmed.substr(0, eqPos).trim() : trimmed.trim();
        if (name) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
        }
      });
      
      console.log('🚨 Emergency cleanup completed');
    } catch (e) {
      console.error('Emergency cleanup failed:', e);
    }
  }
}

// Create a singleton instance
const logoutManager = new LogoutManager();

// Add global helper for emergency cleanup
window.emergencyCleanup = () => logoutManager.emergencyCleanup();

// Export as default and named
export default logoutManager;
export { logoutManager };